import { useEffect } from 'react';
import type { RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

/**
 * Traps Tab focus inside `ref` while it is mounted. On mount it focuses the first
 * focusable element (or the container itself), cycles Tab/Shift+Tab within the
 * container, and restores focus to the previously active element on unmount.
 *
 * The container should carry `tabIndex={-1}` so it can receive focus when it has
 * no focusable children. No-ops when `enabled` is false.
 */
const useFocusTrap = (
  ref: RefObject<HTMLElement | null>,
  enabled = true
): void => {
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const node = ref.current;
    if (!node) {
      return;
    }

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const getFocusable = (): HTMLElement[] =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        // Skip elements hidden via display:none (offsetParent is null then).
        .filter(
          el => el.offsetParent !== null || el === document.activeElement
        );

    // Move focus into the dialog so the trap has an anchor and screen readers
    // announce it — but defer to focus a child already claimed (e.g. an
    // `autoFocus` input) so we don't fight per-modal autofocus.
    if (!node.contains(document.activeElement)) {
      node.focus({ preventScroll: true });
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Tab') {
        return;
      }
      const items = getFocusable();
      if (items.length === 0) {
        // Nothing focusable inside — keep focus on the container.
        event.preventDefault();
        node.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === node)) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    node.addEventListener('keydown', handleKeyDown);
    return () => {
      node.removeEventListener('keydown', handleKeyDown);
      previouslyFocused?.focus?.({ preventScroll: true });
    };
  }, [ref, enabled]);
};

export default useFocusTrap;
